import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import CAlert from 'src/components/CAlert';
import { DialogProvider } from 'src/components/dialog/DialogContext';
import ScrollToTop from 'src/components/scroll-to-top';
import { useStores } from 'src/models';
import { PATH_PAGE } from 'src/routes/paths';
import Login from 'src/screens/auth/login/Login';
import { AxiosInterceptor } from 'src/service/useAxiosInterceptor';
import { MENUSID_CATEGORYSID } from 'src/static/constants';

import LoadingScreen from '../components/loading-screen';

type AuthGuardProps = {
  children: React.ReactNode;
};

function AuthGuard({ children }: AuthGuardProps) {
  const { authStore } = useStores();
  const { isInitialized, isAuthenticated, user } = authStore;

  const { pathname } = useLocation();

  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);

  // 사용자 메뉴 리스트
  const userMenuList = user?.menuList;

  // 사용자 메뉴 권한 확인
  const checkUserAccessiblePath = () => {
    if (!userMenuList) return false;
    const isPathAccessible = userMenuList.some((menu) => menu.menuUri === pathname);
    // 예외 처리: category pathname을 허용(category menuSid: 4)
    const isCategoryPath = userMenuList.some((menu) => menu.menuSid === MENUSID_CATEGORYSID);

    return isPathAccessible || isCategoryPath;
  };

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    if (pathname !== requestedLocation) {
      setRequestedLocation(pathname);
    }
    return <Login />;
  }

  if (requestedLocation && pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  if (authStore.user && !checkUserAccessiblePath()) {
    return <Navigate to={PATH_PAGE.page403} />;
  }

  return (
    <>
      <AxiosInterceptor />
      <CAlert />
      <DialogProvider>
        {children}
        <ScrollToTop />
      </DialogProvider>
    </>
  );
}

export default observer(AuthGuard);
